import apiClient from "../utils/apiClient";
import cityApi from "../api/cityApi";
import orderApi from "../api/orderApi";
import pincodeApi from "../api/pincodeApi";
import { buildCategoryPayload } from "../api/categoryApi";

const toList = (response, fallback = []) => {
  if (Array.isArray(response)) {
    return response;
  }

  return Array.isArray(response?.data) ? response.data : fallback;
};

const getAuthConfig = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("adminToken") ||
    localStorage.getItem("authToken");

  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const requireList = async (request, resourceName) => {
  try {
    const response = await request();
    return toList(response);
  } catch (error) {
    const status = error?.response?.status;
    const detail = status ? ` (${status})` : "";
    throw new Error(`Failed to load ${resourceName}${detail}`);
  }
};

const settleList = async (request, fallback = []) => {
  try {
    const response = await request();
    return toList(response, fallback);
  } catch {
    return fallback;
  }
};

export const getAdminDashboardData = async () => {
  const [orders, categories, addresses, cities, pincodes, admins] = await Promise.all([
    requireList(() => apiClient.get("/api/orders"), "orders"),
    settleList(() => apiClient.get("/api/categories/with-subcategories")),
    settleList(() => apiClient.get("/api/addresses")),
    cityApi.getCities(),
    pincodeApi.getPincodes().catch(() => []),
    settleList(() => apiClient.get("/api/users/admins")),
  ]);

  return { orders, categories, addresses, cities, pincodes, admins };
};

export const getOrders = async () => {
  const response = await orderApi.getAllOrders();
  return toList(response);
};

export const getCustomerAddressesAndOrders = async () => {
  const [addressesResponse, ordersResponse] = await Promise.all([
    apiClient.get("/api/addresses"),
    orderApi.getAllOrders(),
  ]);

  return {
    addresses: toList(addressesResponse),
    orders: toList(ordersResponse),
  };
};

export const getAdmins = async () => {
  const response = await apiClient.get("/api/users/admins");
  return toList(response);
};

export const createCategory = async (payload) => {
  const response = await apiClient.post("/api/categories", buildCategoryPayload(payload), getAuthConfig());
  return response.data;
};

export const updateCategory = async (categoryId, payload) => {
  const response = await apiClient.put(`/api/categories/${categoryId}`, buildCategoryPayload(payload), getAuthConfig());
  return response.data;
};

export const deleteCategory = (categoryId) => apiClient.delete(`/api/categories/${categoryId}`);

export const createSubCategory = async (payload) => {
  const response = await apiClient.post("/api/subcategories", payload, getAuthConfig());
  return response.data;
};

export const updateSubCategory = async (subCategoryId, payload) => {
  const response = await apiClient.put(`/api/subcategories/${subCategoryId}`, payload, getAuthConfig());
  return response.data;
};

export const deleteSubCategory = (subCategoryId) => apiClient.delete(`/api/subcategories/${subCategoryId}`, getAuthConfig());

export const createCity = (payload) => cityApi.createCity(payload);
export const updateCity = (cityId, payload) => cityApi.updateCity(cityId, payload);
export const deleteCity = (cityId) => cityApi.deleteCity(cityId);

export const getPincodes = () => pincodeApi.getPincodes();
export const createPincode = (payload) => pincodeApi.createPincode(payload);
export const updatePincode = (pincodeId, payload) => pincodeApi.updatePincode(pincodeId, payload);
export const deletePincode = (pincodeId) => pincodeApi.deletePincode(pincodeId);

export const adminOrderService = orderApi;
